import { useState, useEffect, useRef } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TIERS, formatPrice, billingMonthLabel, effectivePrice } from '../../lib/billing';
import { EVENTS, formatTime } from '../../lib/vdot';
import { isRelayEvent } from '../../lib/relays';
import { seasonKeyForBenchmark } from '../../lib/seasons';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function MultiSelectDropdown({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function toggle(value) {
    onChange(selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value]
    );
  }

  const displayLabel = selected.length === 0
    ? 'All'
    : selected.length === options.length
    ? 'All'
    : selected.map(v => options.find(o => o.value === v)?.label ?? v).join(', ');

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="form-input form-select"
        style={{ minWidth: 160, textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        onClick={() => setOpen(v => !v)}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{displayLabel}</span>
        <span style={{ fontSize: 10, color: 'var(--gray-400)', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 100,
          background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)', padding: '6px 0', minWidth: '100%', maxHeight: 260, overflowY: 'auto',
        }}>
          {options.map(opt => (
            <label key={opt.value} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px',
              cursor: 'pointer', fontSize: 13, color: 'var(--navy)',
              background: selected.includes(opt.value) ? 'var(--gray-50)' : 'transparent',
            }}>
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                style={{ accentColor: 'var(--gold)', width: 14, height: 14, flexShrink: 0 }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}


function downloadCSV(filename, headers, rows) {
  const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function AdminDashboard() {
  const { profile } = useAuth();
  const now = new Date();

  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  // '' = all coaches, otherwise a coach profile id. Filters the
  // athlete table + the summary cards below it.
  const [selectedCoachId, setSelectedCoachId] = useState('');

  const [athletes, setAthletes] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);

  const [markingPaid, setMarkingPaid] = useState(null); // athleteId
  const [payTier, setPayTier] = useState('core');
  const [payAmountOverride, setPayAmountOverride] = useState(''); // custom amount in dollars, string for input
  const [saving, setSaving] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [reassigning, setReassigning] = useState(null);

  // Custom price editing state: { athleteId, value (dollars string) }
  const [editingPrice, setEditingPrice] = useState(null);
  const [savingPrice, setSavingPrice] = useState(false);

  // Billing start editing state: { athleteId, month, year }
  const [editingBillingStart, setEditingBillingStart] = useState(null);
  const [savingBillingStart, setSavingBillingStart] = useState(false);


  // Top 10 state
  const [top10Events, setTop10Events] = useState(['mile']);
  const [top10Genders, setTop10Genders] = useState([]);  // [] = all
  const [top10Levels, setTop10Levels] = useState([]);    // [] = all
  const [top10Results, setTop10Results] = useState({});  // { [event]: entries[] }
  const [top10Loading, setTop10Loading] = useState(false);

  // Improvement tracking state
  const [improvementAthleteId, setImprovementAthleteId] = useState('');
  const [improvementBenchmarks, setImprovementBenchmarks] = useState([]);
  const [improvementLoading, setImprovementLoading] = useState(false);

  // Athlete import state
  const [importText, setImportText] = useState('');
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const years = [];
  for (let y = 2023; y <= now.getFullYear() + 1; y++) years.push(y);

  useEffect(() => {
    load();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    loadTop10(top10Events, top10Genders, top10Levels);
  }, [top10Events, top10Genders, top10Levels]);

  useEffect(() => {
    if (!improvementAthleteId) { setImprovementBenchmarks([]); return; }
    setImprovementLoading(true);
    supabase
      .from('benchmarks')
      .select('event, time_seconds, race_date, notes, season')
      .eq('athlete_id', improvementAthleteId)
      .then(({ data }) => {
        setImprovementBenchmarks(data ?? []);
        setImprovementLoading(false);
      });
  }, [improvementAthleteId]);

  const LEVEL_GRADES = {
    middle: ['6th','7th','8th'],
    high: ['9th','10th','11th','12th'],
    collegiate: ['collegiate'],
    'post-collegiate': ['post-collegiate'],
    pro: ['pro'],
  };

  async function loadTop10(events, genders, levels) {
    if (!events.length) { setTop10Results({}); return; }
    setTop10Loading(true);

    // Resolve eligible athlete IDs from gender/level filters
    let eligibleIds = null;
    if (genders.length > 0 || levels.length > 0) {
      let apQuery = supabase.from('athlete_profiles').select('id, gender, grade_level');
      if (genders.length > 0) apQuery = apQuery.in('gender', genders);
      if (levels.length > 0) {
        const grades = levels.flatMap(l => LEVEL_GRADES[l] ?? []);
        if (grades.length > 0) apQuery = apQuery.in('grade_level', grades);
      }
      const { data: filteredAps } = await apQuery;
      eligibleIds = (filteredAps ?? []).map(a => a.id);
      if (eligibleIds.length === 0) { setTop10Results({}); setTop10Loading(false); return; }
    }

    // Fetch race results only (notes set = came from race calendar, not manual baseline entries)
    let bQuery = supabase
      .from('benchmarks')
      .select('athlete_id, event, time_seconds, race_date')
      .in('event', events)
      .not('race_date', 'is', null)
      .not('notes', 'is', null)
      .neq('notes', 'Baseline entry')
      .order('time_seconds', { ascending: true });
    if (eligibleIds) bQuery = bQuery.in('athlete_id', eligibleIds);
    const { data: bmarks } = await bQuery;

    // Fetch joined dates for all athletes that appear in results
    const bmarkAthleteIds = [...new Set((bmarks ?? []).map(b => b.athlete_id))];
    const joinedByAthlete = {};
    if (bmarkAthleteIds.length > 0) {
      const { data: apData } = await supabase
        .from('athlete_profiles')
        .select('id, joined_apex_date, billing_start_month, billing_start_year')
        .in('id', bmarkAthleteIds);
      (apData ?? []).forEach(ap => {
        joinedByAthlete[ap.id] = ap.joined_apex_date
          ?? (ap.billing_start_year && ap.billing_start_month
            ? `${ap.billing_start_year}-${String(ap.billing_start_month).padStart(2, '0')}-01`
            : null);
      });
    }

    // Only keep benchmarks that occurred on or after the athlete's
    // joined_apex_date. Exact-date comparison (vs month-rounded) so a
    // race run before the athlete joined — even in the same calendar
    // month — is correctly excluded from the Apex Top 10.
    function isApexPerformance(b) {
      const joined = joinedByAthlete[b.athlete_id];
      if (!joined) return true; // no join info → include
      if (!b.race_date) return false;
      return b.race_date >= joined; // ISO YYYY-MM-DD strings sort lexicographically
    }

    // Group by event, best time per athlete per event, top 10
    const byEvent = {};
    for (const b of bmarks ?? []) {
      if (!isApexPerformance(b)) continue;
      // Relay race events shouldn't appear in Top 10 — splits are the
      // recordable event for a relay leg.
      if (isRelayEvent(b.event)) continue;
      if (!byEvent[b.event]) byEvent[b.event] = new Map();
      if (!byEvent[b.event].has(b.athlete_id)) byEvent[b.event].set(b.athlete_id, b);
    }

    // Collect all unique athlete IDs for name lookup
    const allIds = [...new Set(Object.values(byEvent).flatMap(m => [...m.keys()]))];
    const nameMap = {};
    if (allIds.length > 0) {
      const { data: names } = await supabase.from('profiles').select('id, full_name').in('id', allIds);
      (names ?? []).forEach(n => { nameMap[n.id] = n.full_name; });
    }

    const results = {};
    for (const event of events) {
      const top10 = [...(byEvent[event]?.values() ?? [])].slice(0, 10);
      results[event] = top10.map((b, i) => ({
        rank: i + 1,
        name: nameMap[b.athlete_id] ?? '—',
        time_seconds: b.time_seconds,
        race_date: b.race_date,
      }));
    }
    setTop10Results(results);
    setTop10Loading(false);
  }

  async function handleImport() {
    const lines = importText.split('\n').map(l => l.trim()).filter(Boolean);
    const athletes = lines.map(line => {
      const parts = line.split(',').map(p => p.trim());
      if (parts.length === 1) return { full_name: '', email: parts[0] };
      if (parts.length === 2) return { full_name: parts[0], email: parts[1] };
      return { full_name: parts[0], email: parts[1], role: parts[2] };
    });
    if (!athletes.length) return;
    setImporting(true);
    setImportResults(null);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/import-athletes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ athletes }),
    });
    const json = await res.json();
    setImportResults(json.results ?? []);
    setImporting(false);
  }

  async function load() {
    setLoading(true);

    const { data: athleteProfiles } = await supabase
      .from('athlete_profiles')
      .select('id, coach_id, custom_price_cents, paused, joined_apex_date, billing_start_month, billing_start_year');

    if (!athleteProfiles?.length) {
      setAthletes([]);
      setLoading(false);
      return;
    }

    const athleteIds = athleteProfiles.map(a => a.id);
    const athleteCoachMap = Object.fromEntries(athleteProfiles.map(ap => [ap.id, ap.coach_id]));
    const customPriceMap = Object.fromEntries(athleteProfiles.map(ap => [ap.id, ap.custom_price_cents ?? null]));
    const pausedMap = Object.fromEntries(athleteProfiles.map(ap => [ap.id, ap.paused ?? false]));
    // Prefer the exact `joined_apex_date` but fall back to the legacy
    // month+year columns (as YYYY-MM-01) so rows that haven't been
    // backfilled yet still filter correctly.
    const joinedDateMap = Object.fromEntries(
      athleteProfiles.map(ap => [
        ap.id,
        ap.joined_apex_date
          ?? (ap.billing_start_year && ap.billing_start_month
            ? `${ap.billing_start_year}-${String(ap.billing_start_month).padStart(2, '0')}-01`
            : null),
      ])
    );

    const { data: archivedCoachProfiles } = await supabase.from('coach_profiles').select('id').eq('archived', true);
    const archivedCoachIds = new Set((archivedCoachProfiles ?? []).map(c => c.id));

    const [
      { data: athleteNames },
      { data: coachList },
      { data: assignments },
      { data: billingRows },
    ] = await Promise.all([
      supabase.from('profiles').select('id, full_name, role').in('id', athleteIds).order('full_name'),
      supabase.from('profiles').select('id, full_name').eq('role', 'coach').order('full_name'),
      supabase
        .from('athlete_plan_assignments')
        .select('athlete_id, plans(month, year, status)')
        .in('athlete_id', athleteIds),
      supabase
        .from('billing')
        .select('*')
        .in('athlete_id', athleteIds)
        .eq('month', selectedMonth)
        .eq('year', selectedYear),
    ]);

    const activeCoaches = (coachList ?? []).filter(c => !archivedCoachIds.has(c.id));
    const coachMap = Object.fromEntries(activeCoaches.map(c => [c.id, c]));
    setCoaches(activeCoaches);

    const planStatusMap = {};
    (assignments ?? [])
      .filter(a => a.plans?.month === selectedMonth && a.plans?.year === selectedYear)
      .forEach(a => { planStatusMap[a.athlete_id] = a.plans?.status ?? 'in_progress'; });

    const billingMap = {};
    (billingRows ?? []).forEach(b => { billingMap[b.athlete_id] = b; });

    setAthletes(
      (athleteNames ?? []).map(p => ({
        id: p.id,
        full_name: p.full_name,
        coachId: athleteCoachMap[p.id] ?? null,
        coachName: athleteCoachMap[p.id]
          ? (coachMap[athleteCoachMap[p.id]]?.full_name ?? 'Unknown')
          : null,
        planStatus: planStatusMap[p.id] ?? null, // null = not started
        billing: billingMap[p.id] ?? null,
        customPriceCents: customPriceMap[p.id],
        paused: pausedMap[p.id] ?? false,
        joinedApexDate: joinedDateMap[p.id] ?? null, // YYYY-MM-DD or null
      }))
    );

    setLoading(false);
  }

  // When opening the mark-paid modal, pre-fill amount from custom price or tier
  function openMarkPaid(athlete) {
    const tier = athlete.billing?.tier ?? 'core';
    setMarkingPaid(athlete.id);
    setPayTier(tier);
    const price = effectivePrice(tier, athlete.customPriceCents);
    setPayAmountOverride((price / 100).toFixed(0));
  }

  async function handleMarkPaid(athleteId) {
    setSaving(true);
    setBillingError('');
    const athlete = athletes.find(a => a.id === athleteId);
    const amountCents = Math.round(parseFloat(payAmountOverride) * 100) || effectivePrice(payTier, athlete?.customPriceCents);
    const existing = athlete?.billing;

    let err;
    if (existing) {
      ({ error: err } = await supabase.from('billing').update({
        tier: payTier,
        amount_cents: amountCents,
        paid_at: new Date().toISOString(),
        marked_paid_by: profile.id,
      }).eq('id', existing.id));
    } else {
      ({ error: err } = await supabase.from('billing').insert({
        athlete_id: athleteId,
        month: selectedMonth,
        year: selectedYear,
        tier: payTier,
        amount_cents: amountCents,
        paid_at: new Date().toISOString(),
        marked_paid_by: profile.id,
      }));
    }

    if (err) {
      setBillingError(`Failed to mark paid: ${err.message} (${err.code})`);
      setSaving(false);
      return;
    }

    setMarkingPaid(null);
    await load();
    setSaving(false);
  }

  async function handleUnmarkPaid(billingId) {
    setBillingError('');
    const { error: err } = await supabase
      .from('billing')
      .update({ paid_at: null, marked_paid_by: null })
      .eq('id', billingId);
    if (err) {
      setBillingError(`Failed to unmark paid: ${err.message} (${err.code})`);
      return;
    }
    await load();
  }

  async function handleReassignCoach(athleteId, newCoachId) {
    setReassigning(athleteId);
    await supabase
      .from('athlete_profiles')
      .update({ coach_id: newCoachId || null })
      .eq('id', athleteId);
    await load();
    setReassigning(null);
  }

  async function handleSaveCustomPrice(athleteId) {
    setSavingPrice(true);
    const raw = editingPrice?.value ?? '';
    const dollars = parseFloat(raw);
    const cents = isNaN(dollars) || raw.trim() === '' ? null : Math.round(dollars * 100);
    await supabase
      .from('athlete_profiles')
      .update({ custom_price_cents: cents })
      .eq('id', athleteId);
    setEditingPrice(null);
    await load();
    setSavingPrice(false);
  }

  async function handleClearCustomPrice(athleteId) {
    setSavingPrice(true);
    await supabase.from('athlete_profiles').update({ custom_price_cents: null }).eq('id', athleteId);
    setEditingPrice(null);
    await load();
    setSavingPrice(false);
  }

  async function handleTogglePause(athleteId) {
    await supabase.from('athlete_profiles').update({ paused: true }).eq('id', athleteId);
    await load();
  }

  async function handleSaveBillingStart(athleteId) {
    setSavingBillingStart(true);
    // Write the new exact-date column. Also null out the legacy
    // month/year so the two don't drift — the read path falls back to
    // the legacy columns only when joined_apex_date is missing.
    const { error: bsErr } = await supabase
      .from('athlete_profiles')
      .update({
        joined_apex_date: editingBillingStart.date,
        billing_start_month: null,
        billing_start_year: null,
      })
      .eq('id', athleteId);
    if (bsErr) {
      alert(`Failed to save joined date: ${bsErr.message}`);
      setSavingBillingStart(false);
      return;
    }
    setEditingBillingStart(null);
    await load();
    setSavingBillingStart(false);
  }

  // Active athletes visible for the selected month (paused always
  // excluded). Billing-period filter keeps the original month-level
  // semantic — an athlete who joined 2026-07-15 still appears for
  // July 2026 billing, same as before.
  const displayAthletes = athletes.filter(a => {
    if (a.paused) return false;
    if (selectedCoachId && a.coachId !== selectedCoachId) return false;
    if (!a.joinedApexDate) return true;
    const jy = parseInt(a.joinedApexDate.slice(0, 4), 10);
    const jm = parseInt(a.joinedApexDate.slice(5, 7), 10);
    return (selectedYear * 12 + selectedMonth) >= (jy * 12 + jm);
  });
  const activeAthletes = displayAthletes;

  const paidCount = activeAthletes.filter(a => a.billing?.paid_at).length;
  const planCount = activeAthletes.filter(a => a.planStatus === 'sent').length;
  const totalRevenue = activeAthletes.reduce(
    (sum, a) => sum + (a.billing?.paid_at ? a.billing.amount_cents : 0), 0
  );

  // The effective amount shown in the mark-paid modal
  const markingAthlete = athletes.find(a => a.id === markingPaid);

  return (
    <Layout>
      <div style={{ marginBottom: 24 }}>
        <p className="page-eyebrow">Admin</p>
        <h1 className="page-title">Overview</h1>
      </div>

      {/* Summary stats */}
      <div className="admin-stats-row">
        <div className="admin-stat-card">
          <span className="admin-stat-value">{activeAthletes.length}</span>
          <span className="admin-stat-label">Active Athletes</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">{coaches.length}</span>
          <span className="admin-stat-label">Coaches</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">
            {paidCount}<span className="admin-stat-denom">/{activeAthletes.length}</span>
          </span>
          <span className="admin-stat-label">Paid This Month</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">
            {planCount}<span className="admin-stat-denom">/{activeAthletes.length}</span>
          </span>
          <span className="admin-stat-label">Plans Active</span>
        </div>
        <div className="admin-stat-card">
          <span className="admin-stat-value">{formatPrice(totalRevenue)}</span>
          <span className="admin-stat-label">{MONTHS[selectedMonth - 1]} Revenue</span>
        </div>
      </div>

      {/* Athlete Import */}
      <div className="section-card" style={{ marginBottom: 24 }}>
        <button
          type="button"
          onClick={() => { setImportOpen(v => !v); setImportResults(null); }}
          style={{ width: '100%', background: 'none', border: 'none', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontFamily: 'var(--font)' }}
        >
          <span className="section-card-title" style={{ marginBottom: 0 }}>Import Users</span>
          <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{importOpen ? '▲ Hide' : '▼ Show'}</span>
        </button>
        {importOpen && (
          <div style={{ padding: '0 24px 20px' }}>
            <p style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 12 }}>
              Paste one user per line. Format: <code>Full Name, email@address.com, role</code> — role is optional and defaults to <em>athlete</em>. Valid roles: athlete, coach, parent, pro_athlete.
              All accounts use the temporary password <strong>ApexElite2025!</strong> — users must set their own on first login.
            </p>
            <textarea
              className="form-input"
              rows={6}
              placeholder={"Jane Smith, jane@example.com\nMike Coach, mike@example.com, coach\nSarah Parent, sarah@example.com, parent\nPro Runner, pro@example.com, pro_athlete"}
              value={importText}
              onChange={e => setImportText(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
            />
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                className="btn btn-primary"
                style={{ padding: '9px 24px' }}
                onClick={handleImport}
                disabled={importing || !importText.trim()}
              >
                {importing ? 'Importing…' : 'Import Users'}
              </button>
              {importResults && (
                <span style={{ fontSize: 13, color: 'var(--gray-400)' }}>
                  {importResults.filter(r => r.success).length} succeeded · {importResults.filter(r => !r.success).length} failed
                </span>
              )}
            </div>
            {importResults && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {importResults.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <span style={{ color: r.success ? '#15803d' : '#b91c1c', fontWeight: 700 }}>
                      {r.success ? '✓' : '✗'}
                    </span>
                    <span style={{ color: 'var(--navy)' }}>{r.name ? `${r.name} — ` : ''}{r.email}</span>
                    {!r.success && <span style={{ color: '#b91c1c', fontSize: 12 }}>{r.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Month / year / coach controls */}
      <div className="plan-controls" style={{ marginBottom: 20 }}>
        <div className="form-group">
          <label className="form-label">Month</label>
          <select
            className="form-input form-select"
            value={selectedMonth}
            onChange={e => setSelectedMonth(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Year</label>
          <select
            className="form-input form-select"
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Coach</label>
          <select
            className="form-input form-select"
            value={selectedCoachId}
            onChange={e => setSelectedCoachId(e.target.value)}
          >
            <option value="">All coaches</option>
            {coaches.map(c => (
              <option key={c.id} value={c.id}>{c.full_name}</option>
            ))}
          </select>
        </div>
      </div>

      {billingError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 16px', marginBottom: 12, fontSize: 13, color: '#b91c1c', fontFamily: 'monospace' }}>
          {billingError}
        </div>
      )}

      {/* Athletes table */}
      <div className="section-card">
        {loading ? (
          <div className="loading-screen" style={{ minHeight: 200 }}>
            <div className="loading-dot-row">
              <div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" />
            </div>
          </div>
        ) : athletes.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state-title">No athletes in the system yet</p>
            <p className="empty-state-sub">Athletes appear here once they sign up and are assigned to a coach.</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Athlete</th>
                <th>Coach</th>
                <th>Joined Apex</th>
                <th>Custom Price</th>
                <th>{MONTHS[selectedMonth - 1]} Plan</th>
                <th>Billing</th>
                <th>Tier</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayAthletes.map(athlete => {
                const b = athlete.billing;
                const isPaid = !!b?.paid_at;
                const isEditingThisPrice = editingPrice?.athleteId === athlete.id;
                const isEditingThisStart = editingBillingStart?.athleteId === athlete.id;

                return (
                  <tr key={athlete.id}>
                    <td className="admin-table-name">{athlete.full_name}</td>

                    {/* Coach dropdown */}
                    <td>
                      <select
                        className="admin-coach-select"
                        value={athlete.coachId ?? ''}
                        disabled={reassigning === athlete.id}
                        onChange={e => handleReassignCoach(athlete.id, e.target.value)}
                      >
                        <option value="">— Unassigned —</option>
                        {coaches.map(c => (
                          <option key={c.id} value={c.id}>{c.full_name}</option>
                        ))}
                      </select>
                    </td>

                    {/* Joined Apex (exact date) */}
                    <td>
                      {isEditingThisStart ? (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                          <input
                            type="date"
                            className="admin-coach-select"
                            value={editingBillingStart.date ?? ''}
                            onChange={e => setEditingBillingStart(s => ({ ...s, date: e.target.value }))}
                          />
                          <button
                            className="btn-xs btn-xs-primary"
                            onClick={() => handleSaveBillingStart(athlete.id)}
                            disabled={savingBillingStart || !editingBillingStart.date}
                          >✓</button>
                          <button className="btn-xs btn-xs-ghost" onClick={() => setEditingBillingStart(null)}>✕</button>
                        </div>
                      ) : (
                        <button
                          className="admin-custom-price-btn"
                          onClick={() => setEditingBillingStart({
                            athleteId: athlete.id,
                            date: athlete.joinedApexDate ?? now.toISOString().slice(0, 10),
                          })}
                        >
                          {athlete.joinedApexDate
                            ? new Date(athlete.joinedApexDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : <span style={{ color: 'var(--gray-300)' }}>— set date</span>}
                        </button>
                      )}
                    </td>

                    {/* Custom price */}
                    <td>
                      {isEditingThisPrice ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>$</span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            placeholder="e.g. 150"
                            value={editingPrice.value}
                            onChange={e => setEditingPrice({ athleteId: athlete.id, value: e.target.value })}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveCustomPrice(athlete.id);
                              if (e.key === 'Escape') setEditingPrice(null);
                            }}
                            className="admin-price-input"
                            autoFocus
                          />
                          <button
                            className="btn-xs btn-xs-primary"
                            onClick={() => handleSaveCustomPrice(athlete.id)}
                            disabled={savingPrice}
                          >✓</button>
                          {athlete.customPriceCents != null && (
                            <button
                              className="btn-xs btn-xs-ghost"
                              style={{ color: 'var(--gray-400)', fontSize: 10 }}
                              onClick={() => handleClearCustomPrice(athlete.id)}
                              disabled={savingPrice}
                              title="Remove custom price — revert to tier default"
                            >Reset</button>
                          )}
                          <button
                            className="btn-xs btn-xs-ghost"
                            onClick={() => setEditingPrice(null)}
                          >✕</button>
                        </div>
                      ) : (
                        <button
                          className="admin-custom-price-btn"
                          onClick={() => setEditingPrice({
                            athleteId: athlete.id,
                            value: athlete.customPriceCents != null
                              ? (athlete.customPriceCents / 100).toFixed(0)
                              : '',
                          })}
                        >
                          {athlete.customPriceCents != null
                            ? formatPrice(athlete.customPriceCents)
                            : <span style={{ color: 'var(--gray-300)' }}>— tier default</span>
                          }
                        </button>
                      )}
                    </td>

                    {/* Plan badge */}
                    <td>
                      {athlete.planStatus === 'sent'
                        ? <span className="admin-plan-badge admin-plan-badge--done">Live</span>
                        : athlete.planStatus === 'in_progress'
                          ? <span className="admin-plan-badge admin-plan-badge--progress">In Progress</span>
                          : <span className="admin-plan-badge admin-plan-badge--missing">Not Started</span>
                      }
                    </td>

                    {/* Billing status */}
                    <td>
                      {isPaid
                        ? <span className="billing-badge billing-badge--paid">Paid</span>
                        : b
                          ? <span className="billing-badge billing-badge--pending">Pending</span>
                          : <span className="billing-badge billing-badge--overdue">Unpaid</span>
                      }
                    </td>

                    {/* Tier */}
                    <td style={{ color: 'var(--gray-600)', fontSize: 13 }}>
                      {b ? (TIERS[b.tier]?.label ?? b.tier) : <span className="cell-muted">—</span>}
                      {b && <span style={{ color: 'var(--gray-400)', fontSize: 11, marginLeft: 4 }}>
                        {formatPrice(b.amount_cents)}
                      </span>}
                    </td>

                    {/* Action */}
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {isPaid ? (
                          <button
                            className="btn-xs btn-xs-ghost"
                            onClick={() => handleUnmarkPaid(b.id)}
                          >
                            Unmark
                          </button>
                        ) : (
                          <button
                            className="btn-xs btn-xs-primary"
                            onClick={() => openMarkPaid(athlete)}
                          >
                            Mark Paid
                          </button>
                        )}
                        <button
                          className="btn-xs btn-xs-ghost"
                          onClick={() => handleTogglePause(athlete.id)}
                          title="Pause athlete — reactivate via the Athletes page"
                        >
                          Pause
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Top 10 Lists */}
      <div className="section-card" style={{ marginTop: 28, overflow: 'visible' }}>
        <div className="section-card-header">
          <span className="section-card-title">Top 10</span>
          {Object.keys(top10Results).length > 0 && (
            <button
              className="btn-xs btn-xs-ghost"
              style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
              onClick={() => {
                const rows = [];
                for (const event of top10Events) {
                  for (const entry of (top10Results[event] ?? [])) {
                    rows.push([EVENTS[event]?.label ?? event, entry.rank, entry.name, formatTime(entry.time_seconds), entry.race_date]);
                  }
                }
                downloadCSV(`top-10-${new Date().toISOString().slice(0, 10)}.csv`, ['Event', 'Rank', 'Athlete', 'Time', 'Date'], rows);
              }}
            >
              ↓ Export CSV
            </button>
          )}
        </div>

        {/* Filters row */}
        <div style={{ padding: '12px 24px 8px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontSize: 10 }}>Event</label>
            <MultiSelectDropdown
              label="Event"
              options={Object.entries(EVENTS)
                .filter(([k]) => k !== 'other' && !isRelayEvent(k))
                .map(([k, ev]) => ({ value: k, label: ev.label }))}
              selected={top10Events}
              onChange={setTop10Events}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontSize: 10 }}>Gender</label>
            <MultiSelectDropdown
              label="Gender"
              options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]}
              selected={top10Genders}
              onChange={setTop10Genders}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ fontSize: 10 }}>Level</label>
            <MultiSelectDropdown
              label="Level"
              options={[
                { value: 'middle', label: 'Middle School' },
                { value: 'high', label: 'High School' },
                { value: 'collegiate', label: 'Collegiate' },
                { value: 'post-collegiate', label: 'Post-Collegiate' },
                { value: 'pro', label: 'Apex Elite Pro' },
              ]}
              selected={top10Levels}
              onChange={setTop10Levels}
            />
          </div>
        </div>

        {top10Loading ? (
          <div style={{ padding: '20px 24px', color: 'var(--gray-400)', fontSize: 13 }}>Loading...</div>
        ) : top10Events.length === 0 ? (
          <div style={{ padding: '16px 24px', color: 'var(--gray-400)', fontSize: 13, fontStyle: 'italic' }}>
            Select at least one event.
          </div>
        ) : top10Events.map(event => {
          const entries = top10Results[event] ?? [];
          return (
            <div key={event}>
              {top10Events.length > 1 && (
                <div style={{ padding: '10px 24px 4px', fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--gray-400)' }}>
                  {EVENTS[event]?.label ?? event}
                </div>
              )}
              {entries.length === 0 ? (
                <div style={{ padding: '10px 24px', color: 'var(--gray-400)', fontSize: 13, fontStyle: 'italic' }}>
                  No dated results found for {EVENTS[event]?.label ?? event}.
                </div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>Athlete</th>
                      <th>Time</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(entry => (
                      <tr key={entry.rank}>
                        <td style={{ fontWeight: 700, color: entry.rank <= 3 ? 'var(--gold-dark, #92400e)' : 'var(--gray-400)', fontSize: 13 }}>
                          {entry.rank}
                        </td>
                        <td className="admin-table-name">{entry.name}</td>
                        <td style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', fontFamily: 'monospace' }}>
                          {formatTime(entry.time_seconds)}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--gray-400)' }}>{entry.race_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>

      {/* Improvement Tracking */}
      <div className="section-card" style={{ marginTop: 24, marginBottom: 24 }}>
        <div className="section-card-header">
          <span className="section-card-title">Improvement Tracking</span>
          <button
            className="btn-xs btn-xs-ghost"
            style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
            onClick={async () => {
              // Export all athletes' improvement data
              const { data: allBenchmarks } = await supabase
                .from('benchmarks')
                .select('athlete_id, event, time_seconds, race_date, notes')
                .order('race_date', { ascending: true });
              if (!allBenchmarks?.length) return;
              const allIds = [...new Set(allBenchmarks.map(b => b.athlete_id))];
              const { data: names } = await supabase.from('profiles').select('id, full_name').in('id', allIds);
              const nameMap = {};
              (names ?? []).forEach(n => { nameMap[n.id] = n.full_name; });
              const rows = [];
              // Group by athlete, then event
              const byAthlete = {};
              for (const b of allBenchmarks) {
                if (!byAthlete[b.athlete_id]) byAthlete[b.athlete_id] = [];
                byAthlete[b.athlete_id].push(b);
              }
              for (const [athleteId, benchmarks] of Object.entries(byAthlete)) {
                const athleteName = nameMap[athleteId] ?? '—';
                const byEvent = {};
                for (const b of benchmarks) {
                  if (!byEvent[b.event]) byEvent[b.event] = [];
                  byEvent[b.event].push(b);
                }
                for (const [event, entries] of Object.entries(byEvent)) {
                  const baseline = entries.find(e => e.notes === 'Baseline entry');
                  const races = entries.filter(e => e.notes !== 'Baseline entry').sort((a, b) => a.race_date > b.race_date ? 1 : -1);
                  const best = races.length ? races.reduce((b, e) => e.time_seconds < b.time_seconds ? e : b) : null;
                  const latest = races.length ? races[races.length - 1] : null;
                  const refTime = baseline?.time_seconds ?? (races.length >= 2 ? races[0].time_seconds : null);
                  const improvement = refTime && latest ? refTime - latest.time_seconds : null;
                  rows.push([
                    athleteName,
                    EVENTS[event]?.label ?? event,
                    baseline ? formatTime(baseline.time_seconds) : '—',
                    best ? formatTime(best.time_seconds) : '—',
                    latest ? formatTime(latest.time_seconds) : '—',
                    latest?.race_date ?? '—',
                    improvement !== null ? (improvement > 0 ? `-${formatTime(improvement)}` : improvement < 0 ? `+${formatTime(Math.abs(improvement))}` : 'No change') : '—',
                  ]);
                }
              }
              downloadCSV(`improvement-tracking-${new Date().toISOString().slice(0, 10)}.csv`,
                ['Athlete', 'Event', 'Baseline', 'PR', 'Latest', 'Latest Date', 'Improvement'], rows);
            }}
          >
            ↓ Export All CSV
          </button>
        </div>
        <div style={{ padding: '0 24px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="form-group" style={{ margin: 0, minWidth: 220 }}>
            <label className="form-label" style={{ fontSize: 10 }}>Athlete</label>
            <select
              className="form-input form-select"
              value={improvementAthleteId}
              onChange={e => setImprovementAthleteId(e.target.value)}
            >
              <option value="">— Select athlete —</option>
              {[...activeAthletes].sort((a, b) => a.full_name.localeCompare(b.full_name)).map(a => (
                <option key={a.id} value={a.id}>{a.full_name}</option>
              ))}
            </select>
          </div>
        </div>
        {!improvementAthleteId ? (
          <div style={{ padding: '8px 24px 20px', color: 'var(--gray-400)', fontSize: 13, fontStyle: 'italic' }}>
            Select an athlete to view improvement data.
          </div>
        ) : improvementLoading ? (
          <div style={{ padding: '8px 24px 20px', color: 'var(--gray-400)', fontSize: 13 }}>Loading...</div>
        ) : (() => {
          const athlete = athletes.find(a => a.id === improvementAthleteId);

          function fmtDelta(seconds) {
            const total = Math.round(Math.abs(seconds));
            const m = Math.floor(total / 60);
            const s = total % 60;
            return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
          }

          // Season key uses the explicit `benchmark.season` tag with
          // a date-based fallback for legacy rows. Logic in
          // src/lib/seasons.js.
          const getSeasonKey = b => seasonKeyForBenchmark(b);

          function isBeforeApex(dateStr) {
            const joined = athlete?.joinedApexDate;
            if (!joined || !dateStr) return false;
            return dateStr < joined; // ISO YYYY-MM-DD strings sort lexicographically
          }

          const allSeasonMap = new Map();
          const byEvent = {};
          for (const b of improvementBenchmarks) {
            if (!b.notes) continue;
            const isBaseline = b.notes === 'Baseline entry';
            let ev = b.event;
            if (ev && ev.startsWith('[')) { try { ev = JSON.parse(ev)[0] ?? ev; } catch {} }
            // Drop relay-race-keyed benchmarks if any slipped in —
            // a relay's "result" lives under the split pseudo-event.
            if (isRelayEvent(ev)) continue;
            if (!byEvent[ev]) byEvent[ev] = { baseline: null, seasons: {} };
            if (isBaseline || isBeforeApex(b.race_date)) {
              // Baseline entries always count as pre-apex regardless of date
              if (!byEvent[ev].baseline || b.time_seconds < byEvent[ev].baseline.time_seconds)
                byEvent[ev].baseline = b;
            } else {
              if (!b.race_date) continue;
              const sk = getSeasonKey(b);
              if (!sk) continue;
              allSeasonMap.set(sk.label, sk.sort);
              if (!byEvent[ev].seasons[sk.label] || b.time_seconds < byEvent[ev].seasons[sk.label])
                byEvent[ev].seasons[sk.label] = b.time_seconds;
            }
          }

          const sortedSeasons = [...allSeasonMap.entries()]
            .sort((a, b) => a[1] - b[1])
            .map(([label]) => label);

          const eventEntries = Object.entries(byEvent)
            .filter(([, d]) => d.baseline !== null || Object.keys(d.seasons).length > 0);

          if (eventEntries.length === 0) {
            return (
              <div style={{ padding: '8px 24px 20px', color: 'var(--gray-400)', fontSize: 13, fontStyle: 'italic' }}>
                No benchmark or race results found for this athlete.
              </div>
            );
          }

          return (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Before Apex</th>
                    {sortedSeasons.map(s => <th key={s}>{s}</th>)}
                    <th>Improvement</th>
                  </tr>
                </thead>
                <tbody>
                  {eventEntries.map(([event, data]) => {
                    const eventSeasons = sortedSeasons.filter(s => data.seasons[s]);
                    const latestSeason = eventSeasons.at(-1);
                    const firstSeason = eventSeasons[0];
                    const latestTime = latestSeason ? data.seasons[latestSeason] : null;
                    const refTime = data.baseline
                      ? data.baseline.time_seconds
                      : (eventSeasons.length >= 2 ? data.seasons[firstSeason] : null);
                    const improvement = (refTime !== null && latestTime !== null) ? refTime - latestTime : null;
                    return (
                      <tr key={event}>
                        <td style={{ fontWeight: 700, color: 'var(--navy)', whiteSpace: 'nowrap' }}>
                          {EVENTS[event]?.label ?? event}
                        </td>
                        <td style={{
                          fontFamily: 'monospace',
                          color: data.baseline ? 'var(--gray-500)' : 'var(--gray-300)',
                          fontStyle: data.baseline ? 'normal' : 'italic',
                        }}>
                          {data.baseline ? formatTime(data.baseline.time_seconds) : '—'}
                        </td>
                        {sortedSeasons.map(s => (
                          <td key={s} style={{
                            fontFamily: 'monospace',
                            fontWeight: data.seasons[s] ? 700 : 400,
                            color: data.seasons[s] ? 'var(--navy)' : 'var(--gray-200)',
                          }}>
                            {data.seasons[s] ? formatTime(data.seasons[s]) : '—'}
                          </td>
                        ))}
                        <td>
                          {improvement !== null ? (
                            improvement > 0 ? (
                              <span style={{ background: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: 12, borderRadius: 12, padding: '2px 10px' }}>
                                -{fmtDelta(improvement)}
                              </span>
                            ) : improvement < 0 ? (
                              <span style={{ background: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 12, borderRadius: 12, padding: '2px 10px' }}>
                                +{fmtDelta(Math.abs(improvement))}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>±0</span>
                            )
                          ) : <span style={{ color: 'var(--gray-300)', fontSize: 13 }}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Mark as Paid modal */}
      {markingPaid && (
        <div className="day-modal-overlay" onClick={() => setMarkingPaid(null)}>
          <div className="day-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="day-modal-header">
              <div className="day-modal-date">
                Mark as Paid — {markingAthlete?.full_name}
              </div>
              <button className="day-modal-close" onClick={() => setMarkingPaid(null)}>×</button>
            </div>
            <div className="day-modal-body">
              <div className="form-group">
                <label className="form-label">Plan tier</label>
                <select
                  className="form-input form-select"
                  value={payTier}
                  onChange={e => {
                    setPayTier(e.target.value);
                    const price = effectivePrice(e.target.value, markingAthlete?.customPriceCents);
                    setPayAmountOverride((price / 100).toFixed(0));
                  }}
                >
                  {Object.entries(TIERS).map(([key, t]) => (
                    <option key={key} value={key}>{t.label} — {formatPrice(t.priceCents)}/mo</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">
                  Amount received
                  {markingAthlete?.customPriceCents != null && (
                    <span style={{ color: 'var(--gold-dark)', marginLeft: 6, fontWeight: 400 }}>
                      (custom rate applied)
                    </span>
                  )}
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 14, color: 'var(--gray-500)' }}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="form-input"
                    style={{ maxWidth: 120 }}
                    value={payAmountOverride}
                    onChange={e => setPayAmountOverride(e.target.value)}
                  />
                  <span style={{ fontSize: 13, color: 'var(--gray-400)' }}>/ month</span>
                </div>
              </div>
              <p style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 8 }}>
                Records {formatPrice(Math.round(parseFloat(payAmountOverride || 0) * 100))} received for {billingMonthLabel(selectedMonth, selectedYear)}.
              </p>
            </div>
            <div className="day-modal-footer">
              <button className="btn-xs btn-xs-ghost" onClick={() => setMarkingPaid(null)}>Cancel</button>
              <button
                className="btn-xs btn-xs-primary"
                onClick={() => handleMarkPaid(markingPaid)}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
